import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Wrench, ArrowLeft, Plus, Trash2, Loader2 } from 'lucide-react';
import apiClient from '../../api/client';
import { useToast } from '../../components/ui/toastContext';
import { useConfirm } from '../../components/ui/useConfirm';

interface ToolItem {
  pt_id: string;
  tool_id: string;
  name: string;
  description: string | null;
  alias: string | null;
  is_enabled: boolean;
  schema: Record<string, unknown> | null;
}

export default function ToolConfig() {
  const { agentId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { confirm, dialog } = useConfirm();
  const [tools, setTools] = useState<ToolItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [form, setForm] = useState({
    name: '',
    description: '',
    parameters: '',
  });

  const loadTools = useCallback(async () => {
    if (!agentId) return;
    try {
      const res = await apiClient.get(`/agents/${agentId}/tools`);
      setTools(res.data || []);
    } catch (e) {
      console.error('Failed to load tools:', e);
    } finally {
      setLoading(false);
    }
  }, [agentId]);

  useEffect(() => {
    // 通过本地包装触发异步加载，避免在 effect 体内直接同步调用 setState 闭包
    const tick = () => {
      void loadTools();
    };
    tick();
  }, [loadTools]);

  const addTool = async () => {
    if (!form.name.trim() || !agentId) return;
    setSaving(true);
    try {
      let schema = null;
      if (form.parameters.trim()) {
        try {
          schema = JSON.parse(form.parameters);
        } catch {
          toast.error('参数定义不是有效的 JSON');
          setSaving(false);
          return;
        }
      }
      await apiClient.post(`/agents/${agentId}/tools`, {
        name: form.name.trim(),
        description: form.description.trim(),
        schema,
      });
      toast.success('工具已添加');
      setForm({ name: '', description: '', parameters: '' });
      setShowAdd(false);
      await loadTools();
    } catch (e) {
      const err = e as { response?: { data?: { detail?: unknown } }; message?: unknown };
      const detail = err.response?.data?.detail;
      const msg = (typeof detail === 'string' && detail) || (typeof err.message === 'string' && err.message) || '未知错误';
      toast.error('添加失败: ' + msg);
    } finally {
      setSaving(false);
    }
  };

  const removeTool = async (toolId: string) => {
    if (!agentId) return;
    if (!(await confirm({ title: '确定要删除这个工具吗？', danger: true }))) return;
    setDeletingId(toolId);
    try {
      await apiClient.delete(`/agents/${agentId}/tools/${toolId}`);
      toast.success('工具已删除');
      await loadTools();
    } catch (e) {
      const err = e as { response?: { data?: { detail?: unknown } }; message?: unknown };
      const detail = err.response?.data?.detail;
      const msg = (typeof detail === 'string' && detail) || (typeof err.message === 'string' && err.message) || '未知错误';
      toast.error('删除失败: ' + msg);
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="container">
      <button className="secondary mb-4" onClick={() => navigate(`/agents/${agentId}`)}>
        <ArrowLeft size={16} /> 返回概览
      </button>

      <div className="card mb-4">
        <div className="flex items-center justify-between">
          <div>
            <h2><Wrench size={20} style={{ verticalAlign: 'middle', marginRight: 8 }} />工具配置</h2>
            <p>为 Agent 配置可调用的外部工具和能力</p>
          </div>
          <button className="primary" onClick={() => setShowAdd(!showAdd)}>
            <Plus size={14} /> 添加工具
          </button>
        </div>
      </div>

      {showAdd && (
        <div className="card mb-4">
          <h3>添加新工具</h3>
          <div className="flex flex-col gap-3">
            <div>
              <label className="text-sm text-muted" style={{ display: 'block', marginBottom: 4 }}>工具名称 *</label>
              <input
                value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })}
                placeholder="如：天气查询、计算器、搜索"
              />
            </div>
            <div>
              <label className="text-sm text-muted" style={{ display: 'block', marginBottom: 4 }}>功能描述</label>
              <textarea
                value={form.description}
                onChange={e => setForm({ ...form, description: e.target.value })}
                placeholder="描述这个工具的功能和使用场景"
                rows={3}
              />
            </div>
            <div>
              <label className="text-sm text-muted" style={{ display: 'block', marginBottom: 4 }}>参数定义 (JSON Schema)</label>
              <textarea
                value={form.parameters}
                onChange={e => setForm({ ...form, parameters: e.target.value })}
                placeholder='{"location": {"type": "string", "description": "城市名称"}, "unit": {"type": "string", "enum": ["celsius", "fahrenheit"]}}'
                rows={4}
                style={{ fontFamily: 'monospace' }}
              />
            </div>
            <div className="flex gap-2">
              <button className="primary" onClick={addTool} disabled={saving}>
                {saving ? <><Loader2 size={14} className="inline animate-spin mr-1" /> 保存中...</> : '添加'}
              </button>
              <button className="secondary" onClick={() => setShowAdd(false)}>取消</button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="loading"><div className="spinner" /></div>
      ) : tools.length === 0 ? (
        <div className="card">
          <div className="empty">
            <div className="empty-icon"><Wrench size={48} /></div>
            <h3>暂无工具</h3>
            <p>为 Agent 添加可调用的工具，扩展其能力范围</p>
          </div>
        </div>
      ) : (
        <div className="list">
          {tools.map(tool => (
            <div key={tool.pt_id} className="list-item">
              <div className="list-item-content">
                <div className="flex items-center gap-2">
                  <h3 style={{ margin: 0 }}>{tool.name}</h3>
                  <span className={`badge ${tool.is_enabled ? 'success' : 'secondary'}`}>
                    {tool.is_enabled ? '启用' : '禁用'}
                  </span>
                </div>
                <p style={{ margin: '4px 0 0' }}>{tool.description || '无描述'}</p>
                {tool.schema && (
                  <small className="text-muted" style={{ display: 'block', marginTop: 4, fontFamily: 'monospace' }}>
                    参数: {JSON.stringify(tool.schema)}
                  </small>
                )}
              </div>
              <button
                className="secondary"
                onClick={() => removeTool(tool.tool_id)}
                disabled={deletingId === tool.tool_id}
                style={{ padding: '6px 12px' }}
              >
                {deletingId === tool.tool_id ? (
                  <Loader2 size={14} className="inline animate-spin" />
                ) : (
                  <><Trash2 size={14} /> 删除</>
                )}
              </button>
            </div>
          ))}
        </div>
      )}
      {dialog}
    </div>
  );
}
